import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProductPageHeader from '../components/shared/ProductPageHeader';
import ProductSwitcherModal from '../components/ProductSwitcherModal';
import { getProductDetails } from '../services/productService';
import { getSalesCondition, saveSalesCondition } from '../services/salesConditionService';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { InputField } from '../components/shared/FormComponents';
import DateRangePicker from '../components/DateRangePicker';
import {
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  GiftIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import {
  formatCompactNumber,
  formatPercent,
  getMockMetaInfluencerProfileByCompanyNo,
  getMockMetaInfluencerSummaryByCompanyNo
} from '../mocks/metaGraphInfluencerMock';

// Brand Icons
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YoutubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" stroke="none">
    <path
      fill="currentColor"
      d="M23 12s0-3.4-.4-5c-.2-1-1-1.8-2-2C18.8 4.5 12 4.5 12 4.5s-6.8 0-8.6.5c-1 .2-1.8 1-2 2C1 8.6 1 12 1 12s0 3.4.4 5c.2 1 1 1.8 2 2c1.8.5 8.6.5 8.6.5s6.8 0 8.6-.5c1-.2 1.8-1 2-2c.4-1.6.4-5 .4-5z"
    />
    <path fill="#ffffff" d="M10 15.5v-7l6 3.5-6 3.5z" />
  </svg>
);

const YoutubeShortsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" stroke="none">
    <path
      fill="currentColor"
      d="M17.77 10.32l-1.2-.5L18 9.06a3.74 3.74 0 0 0-3.5-5.33 3.91 3.91 0 0 0-2 1.25L7.23 9.47a3.86 3.86 0 0 0 0 6.64l1.2.5L7 17.38a3.74 3.74 0 0 0 3.5 5.33 3.91 3.91 0 0 0 2-1.25l5.27-4.49a3.86 3.86 0 0 0 0-6.65z"
    />
    <path fill="#ffffff" d="M10.5 9.5l4 2.5-4 2.5v-5z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TiktokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z"/>
  </svg>
);

const NaverBlogIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" stroke="none">
    <path fill="currentColor" d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11z" />
    <path fill="#ffffff" d="M8 8h2l4 6V8h2v8h-2l-4-6v6H8V8z" />
  </svg>
);

// Section Component for consistent styling
const Section = ({ title, icon: Icon, description, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>
    <div className="rounded-t-xl px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-start gap-3">
     
      <div>
        <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        
      </div>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Styled Label Component
const Label = ({ children, required, className = '' }) => (
  <label className={`block text-sm font-medium text-slate-700 mb-1.5 ${className}`}>
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const InfluencerDetailModal = ({ open, profile, onClose }) => {
  if (!open) return null;

  const graph = profile?.graph && typeof profile.graph === 'object' ? profile.graph : {};
  const insights = profile?.insights && typeof profile.insights === 'object' ? profile.insights : {};
  const tags = profile?.tags && typeof profile.tags === 'object' ? profile.tags : {};
  const recent = Array.isArray(insights?.recent_media) ? insights.recent_media : [];
  const topCountries = Array.isArray(insights?.audience?.top_countries) ? insights.audience.top_countries : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-slate-200 flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-bold text-slate-900 truncate">인플루언서 상세 (Meta Graph API 샘플)</div>
            <div className="mt-0.5 text-xs text-slate-500 truncate">{String(profile?.platform || 'INSTAGRAM')}</div>
          </div>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            onClick={onClose}
          >
            닫기
          </button>
        </div>

        <div className="p-5 overflow-auto">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="shrink-0">
              <div className="h-20 w-20 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
                {String(graph?.profile_picture_url || '').trim() ? (
                  <img alt="profile" src={String(graph.profile_picture_url)} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-sm font-bold text-slate-500">
                    {String(graph?.username || '-').trim().charAt(0) || '-'}
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-lg font-extrabold text-slate-900 truncate">{String(graph?.name || '-')}</div>
                {String(graph?.username || '').trim() && (
                  <div className="text-sm font-semibold text-purple-700 truncate">@{String(graph.username)}</div>
                )}
              </div>
              {String(graph?.biography || '').trim() && (
                <div className="mt-1 text-sm text-slate-600 whitespace-pre-wrap break-words">{String(graph.biography)}</div>
              )}

              <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] text-slate-500">팔로워</div>
                  <div className="text-sm font-bold text-slate-900">{formatCompactNumber(graph?.followers_count)}</div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] text-slate-500">게시물</div>
                  <div className="text-sm font-bold text-slate-900">{formatCompactNumber(graph?.media_count)}</div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] text-slate-500">ER</div>
                  <div className="text-sm font-bold text-slate-900">{formatPercent(insights?.engagement_rate, 1)}</div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] text-slate-500">평균 리치</div>
                  <div className="text-sm font-bold text-slate-900">{formatCompactNumber(insights?.avg_reach)}</div>
                </div>
              </div>

              {Array.isArray(tags?.categories) && tags.categories.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {tags.categories.slice(0, 6).map((c) => (
                    <span key={String(c)} className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 px-2 py-1 text-[11px] font-semibold">
                      {String(c)}
                    </span>
                  ))}
                </div>
              )}

              {topCountries.length > 0 && (
                <div className="mt-3 text-xs text-slate-600">
                  <span className="font-semibold text-slate-700">주요 국가</span>
                  <span className="ml-2">
                    {topCountries
                      .slice(0, 3)
                      .map((it) => `${String(it?.country || '')} ${(Number(it?.share) * 100).toFixed(0)}%`)
                      .join(' · ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <div className="text-sm font-bold text-slate-900 mb-2">최근 게시물 (샘플)</div>
            {recent.length === 0 ? (
              <div className="text-sm text-slate-500">최근 게시물 데이터가 없습니다.</div>
            ) : (
              <div className="space-y-2">
                {recent.slice(0, 6).map((m) => (
                  <a
                    key={String(m?.id || Math.random())}
                    href={String(m?.permalink || '#')}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-lg border border-slate-200 p-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-700">{String(m?.media_type || '-')}</div>
                        <div className="mt-1 text-sm text-slate-900 font-semibold truncate">{String(m?.caption || '-')}</div>
                      </div>
                      <div className="text-xs text-slate-400 shrink-0">{String(m?.timestamp || '').slice(0, 10)}</div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SalesConditionSettings() {
  const navigate = useNavigate();
  const { productId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [productName, setProductName] = useState('');
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const [clientRows, setClientRows] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [selectedInvitedCompanyNos, setSelectedInvitedCompanyNos] = useState([]);
  const [invitedRequestNote, setInvitedRequestNote] = useState('');
  const [recruitMode, setRecruitMode] = useState('OPEN');

  const [influencerDetailOpen, setInfluencerDetailOpen] = useState(false);
  const [influencerDetailCompanyNo, setInfluencerDetailCompanyNo] = useState(null);

  const influencerDetailProfile = useMemo(() => {
    if (!influencerDetailCompanyNo) return null;
    return getMockMetaInfluencerProfileByCompanyNo(influencerDetailCompanyNo);
  }, [influencerDetailCompanyNo]);

  const [form, setForm] = useState({
    판매가능시작일: '',
    판매가능종료일: '',
    인플루언서모집시작일: '',
    인플루언서모집종료일: '',
    필수작업물제출기한: '',
    협의가능: false,
    추천타겟고객층: '',
    판매수수료방식: '',
    콘텐츠제작지원: '',
    비고사항: '',
    필수작업물템플릿: {},
  });

  const fetchClientList = async () => {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@사용여부1', 값: false },
        { 컬럼명: '@사용여부2', 값: true },
      ],
    };
    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('거래처 목록을 불러오지 못했습니다.');
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data?.table) ? data.table : [];
  };

  const seIframeRef = useRef(null);
  const [seReady, setSeReady] = useState(false);
  const seGetResolverRef = useRef(null);
  const [seInitialSetKey, setSeInitialSetKey] = useState(0);
  const seInitialSetDoneRef = useRef(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const onMessage = (e) => {
      const expectedSource = seIframeRef.current?.contentWindow;
      if (expectedSource && e.source !== expectedSource) return;
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'SE_READY') {
        setSeReady(true);
        return;
      }
      if (data.type === 'SE_GET_RESULT') {
        const resolver = seGetResolverRef.current;
        seGetResolverRef.current = null;
        if (resolver) resolver({ html: String(data.html || '') });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!influencerDetailOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setInfluencerDetailOpen(false);
        setInfluencerDetailCompanyNo(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [influencerDetailOpen]);

  const getEditorHtml = () => {
    return new Promise((resolve) => {
      if (!seReady) return resolve({ html: String(form.비고사항 || '') });
      const w = seIframeRef.current?.contentWindow;
      if (!w) return resolve({ html: String(form.비고사항 || '') });
      seGetResolverRef.current = resolve;
      try {
        w.postMessage({ type: 'SE_GET' }, '*');
      } catch {
        seGetResolverRef.current = null;
        resolve({ html: String(form.비고사항 || '') });
        return;
      }
      setTimeout(() => {
        if (seGetResolverRef.current === resolve) {
          seGetResolverRef.current = null;
          resolve({ html: String(form.비고사항 || '') });
        }
      }, 2500);
    });
  };

  const normalizedProductId = useMemo(() => {
    const n = Number(productId);
    return Number.isFinite(n) ? n : null;
  }, [productId]);

  useEffect(() => {
    const load = async () => {
      if (!normalizedProductId) {
        setError('상품번호가 올바르지 않습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const { companyNo } = getUserInfo() || {};

        setClientLoading(true);
        setClientError('');

        const [detail, sales, clients] = await Promise.all([
          getProductDetails(normalizedProductId, companyNo).catch(() => null),
          getSalesCondition({ companyNo, productNo: normalizedProductId }).catch(() => null),
          fetchClientList().catch(() => null),
        ]);

        if (!mountedRef.current) return;

        setProductName(detail?.상품명 || '');
        const clientList = Array.isArray(clients) ? clients : [];
        setClientRows(clientList);

        const rawInvited = Array.isArray(sales?.지정거래처목록)
          ? sales.지정거래처목록.map((v) => Number(v)).filter((n) => Number.isFinite(n) && n > 0)
          : [];
        const normalizedInvited = rawInvited
          .map((v) => {
            // New standard: 업체번호2 (CRS companyNo)
            if (clientList.some((r) => Number(r?.업체번호2) === v)) return v;
            // Legacy: 거래처번호 -> map to 업체번호2
            const hit = clientList.find((r) => Number(r?.거래처번호) === v);
            const mapped = Number(hit?.업체번호2);
            return Number.isFinite(mapped) && mapped > 0 ? mapped : v;
          })
          .filter((v) => Number.isFinite(v) && v > 0);
        setSelectedInvitedCompanyNos(Array.from(new Set(normalizedInvited)));
        setRecruitMode(normalizedInvited.length > 0 ? 'INVITED' : 'OPEN');
        setForm({
          판매가능시작일: sales?.판매가능시작일 || '',
          판매가능종료일: sales?.판매가능종료일 || '',
          인플루언서모집시작일: sales?.인플루언서모집시작일 || '',
          인플루언서모집종료일: sales?.인플루언서모집종료일 || '',
          필수작업물제출기한: sales?.필수작업물제출기한 || '',
          협의가능: !!sales?.협의가능,
          추천타겟고객층: sales?.추천타겟고객층 || '',
          판매수수료방식: sales?.판매수수료방식 || '',
          콘텐츠제작지원: sales?.콘텐츠제작지원 || '',
          비고사항: sales?.비고사항 || '',
          필수작업물템플릿:
            sales?.필수작업물템플릿 && typeof sales.필수작업물템플릿 === 'object' ? sales.필수작업물템플릿 : {},
        });

        seInitialSetDoneRef.current = false;
        setSeInitialSetKey((k) => k + 1);
      } catch (e) {
        if (!mountedRef.current) return;
        setError('판매조건 정보를 불러오지 못했습니다.');
        setClientError('거래처 목록을 불러오지 못했습니다.');
      } finally {
        if (!mountedRef.current) return;
        setLoading(false);
        setClientLoading(false);
      }
    };

    load();
  }, [normalizedProductId]);

  useEffect(() => {
    if (!seReady) return;
    if (seInitialSetDoneRef.current) return;
    try {
      const w = seIframeRef.current?.contentWindow;
      if (!w) return;
      w.postMessage({ type: 'SE_SET', html: String(form.비고사항 || '') }, '*');
      seInitialSetDoneRef.current = true;
    } catch {
      // ignore
    }
  }, [seReady, seInitialSetKey, form.비고사항]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onSave = async () => {
    if (!normalizedProductId) return;
    try {
      setSaving(true);
      setError('');
      const { companyNo, userNo } = getUserInfo() || {};

      const deliverableTemplate =
        form.필수작업물템플릿 && typeof form.필수작업물템플릿 === 'object' ? form.필수작업물템플릿 : {};
      const deliverableEntries = Object.entries(deliverableTemplate).filter(([k, v]) => {
        if (!k) return false;
        return !!v && typeof v === 'object';
      });
      if (deliverableEntries.length === 0) {
        setError('필수 작업물을 1개 이상 지정해주세요.');
        return;
      }

      const invalidCountKeys = deliverableEntries
        .filter(([_, v]) => {
          const n = Number(v?.requiredCount);
          return !Number.isFinite(n) || n <= 0;
        })
        .map(([k]) => String(k));
      if (invalidCountKeys.length > 0) {
        setError('필수 작업물의 개수는 1 이상으로 입력해주세요.');
        return;
      }

      const { html } = await getEditorHtml();
      const noteHtml = String(html || '');
      setForm((prev) => ({ ...prev, 비고사항: noteHtml }));

      const invitedEnabled = String(recruitMode || '').toUpperCase() === 'INVITED';

      const selectedSet = new Set(
        (Array.isArray(selectedInvitedCompanyNos) ? selectedInvitedCompanyNos : []).map((n) => Number(n))
      );
      const invitedCompanies = invitedEnabled
        ? (Array.isArray(clientRows) ? clientRows : [])
            .map((r) => {
              const crsCompanyNo = Number(r?.업체번호2);
              const name = String(r?.거래처명 || '').trim();
              return { companyNo: crsCompanyNo, companyName: name };
            })
            .filter((it) => Number.isFinite(it.companyNo) && it.companyNo > 0 && selectedSet.has(it.companyNo))
        : [];

      await saveSalesCondition({
        companyNo,
        userNo,
        productNo: normalizedProductId,
        invitedRequestNote: invitedEnabled ? invitedRequestNote : '',
        salesCondition: {
          상품명: productName,
          판매가능시작일: form.판매가능시작일,
          판매가능종료일: form.판매가능종료일,
          인플루언서모집시작일: form.인플루언서모집시작일,
          인플루언서모집종료일: form.인플루언서모집종료일,
          필수작업물제출기한: form.필수작업물제출기한,
          협의가능: !!form.협의가능,
          추천타겟고객층: form.추천타겟고객층,
          판매수수료방식: form.판매수수료방식,
          콘텐츠제작지원: form.콘텐츠제작지원,
          비고사항: noteHtml,
          필수작업물템플릿: form.필수작업물템플릿,
          지정거래처목록: invitedEnabled
            ? Array.isArray(selectedInvitedCompanyNos)
              ? selectedInvitedCompanyNos.map((n) => Number(n)).filter((v) => Number.isFinite(v) && v > 0)
              : []
            : [],
          지정거래처상세목록: invitedCompanies,
        },
      });
      setInvitedRequestNote('');
      window.alert('성공적으로 저장되었습니다.');
    } catch (e) {
      console.error('saveSalesCondition failed', e);
      setError('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  const filteredClientRows = useMemo(() => {
    const list = (Array.isArray(clientRows) ? clientRows : []).filter((r) => {
      const no = Number(r?.업체번호2);
      return Number.isFinite(no) && no > 0;
    });
    const q = String(clientFilter || '').trim().toLowerCase();
    const activeOnly = list.filter((r) => !!r?.사용여부);
    if (!q) return activeOnly;
    return activeOnly.filter((r) => {
      const name = String(r?.거래처명 || '').toLowerCase();
      const email = String(r?.대표이메일 || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [clientRows, clientFilter]);

  const openBasicSettings = () => {
    navigate('/products/tour');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-slate-50/50">
      <InfluencerDetailModal
        open={influencerDetailOpen}
        profile={influencerDetailProfile}
        onClose={() => {
          setInfluencerDetailOpen(false);
          setInfluencerDetailCompanyNo(null);
        }}
      />
      <ProductSwitcherModal
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        currentProductId={normalizedProductId}
        onlyTour={true}
        onSelect={(id) => {
          setSwitcherOpen(false);
          navigate(`/sales-condition/${id}`);
        }}
      />

      <div className="px-3 pt-2">
        <ProductPageHeader
          productName={productName}
          productId={normalizedProductId || productId}
          currentPage="salesCondition"
          productType="tour"
          onProductNameClick={() => setSwitcherOpen(true)}
          onBasicSettingsClick={openBasicSettings}
          onBackClick={() => navigate(-1)}
          rightActions={
            <button
              type="button"
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all shadow-sm ${
                saving
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'text-white bg-blue-600 hover:bg-blue-700 hover:shadow-md'
              }`}
              onClick={onSave}
              disabled={saving || loading}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  저장 중...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  저장
                </>
              )}
            </button>
          }
        />
      </div>

      <div className="flex-1 overflow-auto px-4 py-6">
        <div className="max-w-5xl mx-auto space-y-6">
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-700">
              <ExclamationCircleIcon className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          {/* 1. Schedule Section */}
          <Section 
            title="일정 및 기간 설정" 
            icon={CalendarIcon}
            description="상품의 판매 기간과 인플루언서 모집 기간을 설정합니다."
            className="relative z-30"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <Label>판매 가능 기간</Label>
                <DateRangePicker
                  startDate={form.판매가능시작일}
                  endDate={form.판매가능종료일}
                  onStartDateChange={(v) => setForm((prev) => ({ ...prev, 판매가능시작일: v }))}
                  onEndDateChange={(v) => setForm((prev) => ({ ...prev, 판매가능종료일: v }))}
                  disabled={saving || loading}
                  singleCalendar
                />
              </div>
              <div>
                <Label>인플루언서 모집 기간</Label>
                <DateRangePicker
                  startDate={form.인플루언서모집시작일}
                  endDate={form.인플루언서모집종료일}
                  onStartDateChange={(v) => setForm((prev) => ({ ...prev, 인플루언서모집시작일: v }))}
                  onEndDateChange={(v) => setForm((prev) => ({ ...prev, 인플루언서모집종료일: v }))}
                  disabled={saving || loading}
                  singleCalendar
                />
              </div>
            </div>
          </Section>

          {/* 2. Conditions Section */}
          <Section 
            title="판매 및 제공 조건" 
            icon={GiftIcon}
            description="인플루언서에게 제공되는 혜택과 타겟 고객층을 설정합니다."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">조건 협의 가능 여부</h4>
                    <p className="text-xs text-slate-500 mt-1">인플루언서가 제안한 조건에 대해 협의가 가능한지 설정합니다.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="협의가능"
                      checked={!!form.협의가능}
                      onChange={onChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              <div>
                <Label>추천 타겟 고객층</Label>
                <InputField
                  id="추천타겟고객층-input"
                  name="추천타겟고객층"
                  value={form.추천타겟고객층 || ''}
                  onChange={onChange}
                  placeholder="예) 2030 자유여행 선호 고객"
                />
              </div>

              <div>
                <Label>판매 수수료 방식</Label>
                <InputField
                  id="판매수수료방식-input"
                  name="판매수수료방식"
                  value={form.판매수수료방식 || ''}
                  onChange={onChange}
                  placeholder="예) 정률 10%"
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <Label>콘텐츠 제작 지원</Label>
                <InputField
                  id="콘텐츠제작지원-input"
                  name="콘텐츠제작지원"
                  value={form.콘텐츠제작지원 || ''}
                  onChange={onChange}
                  placeholder="예) 사진 10장 제공 / 영상 제작비 30만원 지원"
                />
              </div>
            </div>
          </Section>

          {/* 3. Deliverables Section */}
          <Section 
            title="필수 작업물 (콘텐츠)" 
            icon={DocumentTextIcon}
            description="인플루언서가 필수로 제출해야 하는 콘텐츠를 설정합니다."
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(() => {
                  const DELIVERABLES_CONFIG = [
                    { key: 'INSTAGRAM_FEED', label: '인스타그램 피드', icon: InstagramIcon, color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-200' },
                    { key: 'INSTAGRAM_REELS', label: '인스타그램 릴스', icon: InstagramIcon, color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-200' },
                    { key: 'YOUTUBE_VIDEO', label: '유튜브 영상(롱폼)', icon: YoutubeIcon, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
                    { key: 'YOUTUBE_SHORTS', label: '유튜브 쇼츠', icon: YoutubeShortsIcon, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
                    { key: 'X_CONTENT', label: '엑스 콘텐츠', icon: XIcon, color: 'text-slate-800', bg: 'bg-slate-50', border: 'border-slate-200' },
                    { key: 'TIKTOK_CONTENT', label: '틱톡 콘텐츠', icon: TiktokIcon, color: 'text-black', bg: 'bg-slate-50', border: 'border-slate-200' },
                    { key: 'NAVER_BLOG', label: '네이버 블로그', icon: NaverBlogIcon, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
                  ];

                  return DELIVERABLES_CONFIG.map((config) => {
                    const { key, label, color, bg, border, icon: ChannelIcon } = config;
                    const template = form.필수작업물템플릿 && typeof form.필수작업물템플릿 === 'object' ? form.필수작업물템플릿 : {};
                    const cfg = template[key] && typeof template[key] === 'object' ? template[key] : {};
                    const requiredCount = String(cfg?.requiredCount || '');
                    const checked = !!template[key];

                    return (
                      <div 
                        key={key} 
                        className={`relative rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          checked 
                            ? `${border} ${bg} shadow-sm ring-1 ring-offset-1 ${border.replace('border-', 'ring-')}` 
                            : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <ChannelIcon className={`w-5 h-5 ${color}`} />
                              <span className={`font-semibold ${checked ? 'text-slate-900' : 'text-slate-500'}`}>{label}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div
                                className={`flex items-center gap-1 transition-opacity ${checked ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                              >
                                <span className="text-[11px] font-medium text-slate-600">개수</span>
                                <input
                                  type="text"
                                  value={checked ? requiredCount : ''}
                                  onChange={(e) => {
                                    const next = String(e.target.value || '').replace(/[^0-9]/g, '');
                                    setForm((prev) => {
                                      const cur =
                                        prev.필수작업물템플릿 && typeof prev.필수작업물템플릿 === 'object'
                                          ? prev.필수작업물템플릿
                                          : {};
                                      const existing = cur[key] && typeof cur[key] === 'object' ? cur[key] : {};
                                      if (!cur[key]) return prev;
                                      return {
                                        ...prev,
                                        필수작업물템플릿: {
                                          ...cur,
                                          [key]: {
                                            ...existing,
                                            type: key,
                                            requiredCount: next ? Number(next) : 0
                                          }
                                        }
                                      };
                                    });
                                  }}
                                  className="w-16 px-2 py-1 bg-white border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="0"
                                  disabled={!checked || saving || loading}
                                />
                              </div>

                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setForm((prev) => {
                                    const cur =
                                      prev.필수작업물템플릿 && typeof prev.필수작업물템플릿 === 'object'
                                        ? prev.필수작업물템플릿
                                        : {};
                                    if (cur[key]) {
                                      const next = { ...cur };
                                      delete next[key];
                                      return { ...prev, 필수작업물템플릿: next };
                                    }

                                    const existing = cur[key] && typeof cur[key] === 'object' ? cur[key] : {};
                                    const defaultCountRaw = existing?.requiredCount;
                                    const defaultCount = Number(String(defaultCountRaw ?? '').trim() || 0);
                                    return {
                                      ...prev,
                                      필수작업물템플릿: {
                                        ...cur,
                                        [key]: {
                                          ...existing,
                                          type: key,
                                          requiredCount: Number.isFinite(defaultCount) && defaultCount > 0 ? defaultCount : 1
                                        }
                                      }
                                    };
                                  });
                                }}
                                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                disabled={saving || loading}
                              />
                            </div>
                          </div>
                          
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="w-full md:w-1/3">
                  <Label>필수 작업물 제출 기한</Label>
                  <InputField
                    id="필수작업물제출기한-input"
                    type="date"
                    name="필수작업물제출기한"
                    value={form.필수작업물제출기한 || ''}
                    onChange={onChange}
                    disabled={saving || loading}
                  />
                  <p className="text-xs text-slate-400 mt-1">인플루언서가 콘텐츠를 업로드해야 하는 마감일입니다.</p>
                </div>
              </div>
            </div>
          </Section>

          {/* 4. Recruitment Section */}
          <Section 
            title="모집 방식 설정" 
            icon={UserGroupIcon}
            description="인플루언서를 공개 모집하거나 특정 인플루언서를 지정하여 제안합니다."
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                    String(recruitMode || '').toUpperCase() === 'OPEN'
                      ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                  onClick={() => setRecruitMode('OPEN')}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${String(recruitMode || '').toUpperCase() === 'OPEN' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                      <UserGroupIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">공개 모집 (Open)</h4>
                      <p className="text-sm text-slate-500">모든 인플루언서가 이 상품을 보고 지원할 수 있습니다.</p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                    String(recruitMode || '').toUpperCase() === 'INVITED'
                      ? 'border-purple-500 bg-purple-50/50 ring-1 ring-purple-500'
                      : 'border-slate-200 hover:border-purple-300'
                  }`}
                  onClick={() => setRecruitMode('INVITED')}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${String(recruitMode || '').toUpperCase() === 'INVITED' ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                      <CheckCircleIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">지정 인플루언서 (Invited Only)</h4>
                      <p className="text-sm text-slate-500">선택한 특정 인플루언서에게만 제안을 보냅니다.</p>
                    </div>
                  </div>
                </div>
              </div>

              {String(recruitMode || '').toUpperCase() === 'INVITED' && (
                <div className="animate-fadeIn space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <h5 className="font-semibold text-slate-700 flex items-center gap-2">
                      <MagnifyingGlassIcon className="w-4 h-4" />
                      인플루언서 검색 및 선택
                    </h5>
                    <div className="relative w-full sm:w-[300px]">
                      <input
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="거래처명 또는 이메일로 검색..."
                        disabled={saving || loading || clientLoading}
                      />
                      <MagnifyingGlassIcon className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                    </div>
                  </div>

                  {clientError && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{clientError}</div>}

                  <div className="border border-slate-200 rounded-lg bg-white h-[280px] overflow-hidden flex flex-col">
                    <div className="p-2 bg-slate-100 border-b border-slate-200 text-xs font-semibold text-slate-600 flex justify-between">
                      <span>검색 결과</span>
                      <span>선택됨: {Array.isArray(selectedInvitedCompanyNos) ? selectedInvitedCompanyNos.length : 0}명</span>
                    </div>
                    <div className="overflow-y-auto flex-1 p-2 space-y-1">
                      {clientLoading ? (
                        <div className="h-full flex items-center justify-center text-slate-500 text-sm">목록을 불러오는 중...</div>
                      ) : filteredClientRows.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-slate-400 text-sm">검색 결과가 없습니다.</div>
                      ) : (
                        filteredClientRows.map((r, idx) => {
                          const no = Number(r?.업체번호2);
                          const checked = Number.isFinite(no) && no > 0 && (selectedInvitedCompanyNos || []).includes(no);
                          const metaSummary = getMockMetaInfluencerSummaryByCompanyNo(no);
                          return (
                            <label
                              key={`${no}-${idx}`}
                              className={`relative flex items-start gap-3 px-3 py-2.5 pr-20 rounded-lg cursor-pointer transition-colors ${
                                checked ? 'bg-purple-50 border border-purple-100' : 'hover:bg-slate-50 border border-transparent'
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 rounded border-gray-300"
                                checked={!!checked}
                                disabled={!Number.isFinite(no) || no <= 0 || saving || loading}
                                onChange={() => {
                                  if (!Number.isFinite(no) || no <= 0) return;
                                  setSelectedInvitedCompanyNos((prev) => {
                                    const cur = Array.isArray(prev) ? prev : [];
                                    const exists = cur.includes(no);
                                    if (exists) return cur.filter((v) => v !== no);
                                    return [...cur, no];
                                  });
                                }}
                              />

                              <div className="min-w-0 flex-1">
                                <div className={`text-sm font-medium truncate ${checked ? 'text-purple-900' : 'text-slate-900'}`}>
                                  {String(r?.거래처명 || '-')}
                                </div>
                                <div className="text-xs text-slate-500 truncate">{String(r?.대표이메일 || '')}</div>
                                {metaSummary && (
                                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-600">
                                    {String(metaSummary?.username || '').trim() && (
                                      <span className="font-semibold text-purple-700">@{String(metaSummary.username)}</span>
                                    )}
                                    <span className="text-slate-300">|</span>
                                    <span>팔로워 {formatCompactNumber(metaSummary?.followersCount)}</span>
                                    <span className="text-slate-300">|</span>
                                    <span>ER {formatPercent(metaSummary?.engagementRate, 1)}</span>
                                    {Array.isArray(metaSummary?.categories) && metaSummary.categories.length > 0 && (
                                      <>
                                        <span className="text-slate-300">|</span>
                                        <span className="truncate">{metaSummary.categories.slice(0, 3).join(', ')}</span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>

                              <button
                                type="button"
                                className="absolute right-3 top-3 text-xs font-semibold text-purple-700 hover:text-purple-900 hover:underline"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!Number.isFinite(no) || no <= 0) return;
                                  setInfluencerDetailCompanyNo(no);
                                  setInfluencerDetailOpen(true);
                                }}
                                disabled={!Number.isFinite(no) || no <= 0}
                              >
                                자세히보기
                              </button>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>인플루언서 전달 코멘트</Label>
                    <textarea
                      className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      rows={3}
                      value={invitedRequestNote}
                      onChange={(e) => setInvitedRequestNote(e.target.value)}
                      placeholder="지정한 인플루언서에게 전달할 계약 관련 코멘트를 입력하세요."
                      disabled={saving || loading}
                    />
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* 5. Notes Section */}
          <Section 
            title="상세 비고사항" 
            icon={ChatBubbleLeftRightIcon}
            description="추가적인 안내사항이나 상세 정보를 입력합니다."
          >
            <div className="w-full border border-slate-200 rounded-lg overflow-hidden bg-white shadow-inner" style={{ height: 400 }}>
              <iframe
                ref={seIframeRef}
                title="sales-condition-note-editor"
                src="/smarteditor/SmartEditor4.html"
                className="w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
